import { useMemo, useRef, useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar, ChevronRight, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CollaborativeEditor,
  CollaborativeEditorRef,
} from "@/components/editors/CollaborativeEditor";
import { MeetingNoteTagPicker } from "./MeetingNoteTagPicker";
import { ManagersOnlyToggle } from "@/components/ManagersOnly";
import {
  MeetingNoteWithTags,
  useUpdateMeetingNote,
  useDeleteMeetingNote,
  useMeetingNoteTags,
} from "@/hooks/useMeetingNotes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

interface MeetingNoteCardProps {
  note: MeetingNoteWithTags;
  initialEditing?: boolean;
  collapsible?: boolean;
  onDeleted?: () => void;
}

function getMeetingNoteOpenStorageKey(noteId: string) {
  return `meeting-note-card-open:${noteId}`;
}

function areStringSetsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const aSet = new Set(a);
  return b.every((value) => aSet.has(value));
}

function normalizeIDs(ids: string[]) {
  return Array.from(new Set(ids)).sort();
}

export function MeetingNoteCard({
  note,
  initialEditing = false,
  collapsible = false,
  onDeleted,
}: MeetingNoteCardProps) {
  const [title, setTitle] = useState(note.title);
  const [editDate, setEditDate] = useState<Date>(new Date(note.meeting_date));
  const [editTagIds, setEditTagIds] = useState<string[]>(
    note.tags.map((t) => t.id),
  );
  const [editorOpen, setEditorOpen] = useState(() => {
    if (!collapsible) return true;
    if (initialEditing) return true;

    try {
      return (
        localStorage.getItem(getMeetingNoteOpenStorageKey(note.id)) === "true"
      );
    } catch {
      return false;
    }
  });
  const titleDirtyRef = useRef(false);
  const dateDirtyRef = useRef(false);
  const tagsDirtyRef = useRef(false);
  const editorRef = useRef<CollaborativeEditorRef>(null);
  const { data: allTags = [] } = useMeetingNoteTags();
  const updateMeetingNote = useUpdateMeetingNote();
  const deleteMeetingNote = useDeleteMeetingNote();
  const serverTagIds = useMemo(
    () => normalizeIDs(note.tags.map((tag) => tag.id)),
    [note.tags],
  );

  useEffect(() => {
    if (!titleDirtyRef.current) {
      setTitle(note.title);
      return;
    }
    if (title === note.title) {
      titleDirtyRef.current = false;
    }
  }, [note.title, title]);

  useEffect(() => {
    if (!dateDirtyRef.current) {
      setEditDate(new Date(note.meeting_date));
      return;
    }
    if (format(editDate, "yyyy-MM-dd") === note.meeting_date) {
      dateDirtyRef.current = false;
    }
  }, [editDate, note.meeting_date]);

  useEffect(() => {
    if (!tagsDirtyRef.current) {
      setEditTagIds(serverTagIds);
      return;
    }
    if (areStringSetsEqual(editTagIds, serverTagIds)) {
      tagsDirtyRef.current = false;
    }
  }, [editTagIds, serverTagIds]);

  useEffect(() => {
    if (!collapsible) {
      setEditorOpen(true);
      return;
    }

    if (initialEditing) {
      setEditorOpen(true);
      return;
    }

    try {
      setEditorOpen(
        localStorage.getItem(getMeetingNoteOpenStorageKey(note.id)) === "true",
      );
    } catch {
      setEditorOpen(false);
    }
  }, [collapsible, initialEditing, note.id]);

  useEffect(() => {
    if (!collapsible) return;

    try {
      localStorage.setItem(
        getMeetingNoteOpenStorageKey(note.id),
        String(editorOpen),
      );
    } catch {
      // Storage unavailable — keep the in-memory state only.
    }
  }, [collapsible, editorOpen, note.id]);

  const handleTitleChange = (value: string) => {
    titleDirtyRef.current = true;
    setTitle(value);
  };

  const handleTitleBlur = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== note.title) {
      updateMeetingNote.mutate({
        id: note.id,
        updates: { title: trimmed },
      });
      setTitle(trimmed);
      return;
    }
    titleDirtyRef.current = false;
    setTitle(note.title);
  };

  const handleDateChange = (date: Date | undefined) => {
    if (!date) return;
    const formattedDate = format(date, "yyyy-MM-dd");
    dateDirtyRef.current = true;
    setEditDate(date);
    if (formattedDate === note.meeting_date) {
      dateDirtyRef.current = false;
      return;
    }
    updateMeetingNote.mutate({
      id: note.id,
      updates: { meeting_date: formattedDate },
    });
  };

  const handleTagsChange = (tagIds: string[]) => {
    const nextTagIds = normalizeIDs(tagIds);
    tagsDirtyRef.current = true;
    setEditTagIds(nextTagIds);
    if (areStringSetsEqual(nextTagIds, serverTagIds)) {
      tagsDirtyRef.current = false;
      return;
    }
    updateMeetingNote.mutate({
      id: note.id,
      updates: { tag_ids: nextTagIds },
    });
  };

  const handleAdminOnlyChange = (adminOnly: boolean) => {
    updateMeetingNote.mutate({
      id: note.id,
      updates: { admin_only: adminOnly },
    });
  };

  const handleDelete = () => {
    deleteMeetingNote.mutate(note.id, { onSuccess: onDeleted });
  };

  return (
    <Card
      id={`meeting-note-${note.id}`}
      className="scroll-mt-24"
      data-testid={`meeting-note-card-${note.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-start gap-2">
              {collapsible && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-0.5 h-8 w-8 shrink-0"
                  onClick={() => setEditorOpen((open) => !open)}
                  aria-label={
                    editorOpen ? "Collapse meeting note" : "Expand meeting note"
                  }
                >
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 transition-transform",
                      editorOpen && "rotate-90",
                    )}
                  />
                </Button>
              )}

              <Input
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                onBlur={handleTitleBlur}
                className="text-xl font-semibold border-transparent hover:border-input focus:border-input transition-colors"
                placeholder="Meeting note title"
                autoFocus={initialEditing}
                data-testid={`meeting-note-title-${note.id}`}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7">
                    <Calendar className="h-3 w-3 mr-1" />
                    {format(editDate, "dd.MM.yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={editDate}
                    onSelect={handleDateChange}
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>

              <MeetingNoteTagPicker
                tags={allTags}
                selectedTagIds={editTagIds}
                onChange={handleTagsChange}
              />

              <ManagersOnlyToggle
                adminOnly={note.admin_only}
                onChange={handleAdminOnlyChange}
              />
            </div>
          </div>

          <div className="flex items-center gap-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`meeting-note-delete-${note.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete meeting note?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the meeting note.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    data-testid={`meeting-note-confirm-delete-${note.id}`}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>

      {(!collapsible || editorOpen) && (
        <CardContent>
          <CollaborativeEditor
            ref={editorRef}
            documentId={note.document_id}
            editable={true}
            managersOnly={note.admin_only}
            placeholder="Type @ to mention people..."
          />
        </CardContent>
      )}
    </Card>
  );
}
